import { JobStage, JobStatus, MappingJob } from './mappingJobsSlice';

/** How long a locally-inserted pending job is preserved when the server poll
 * doesn't yet include it. Comfortably longer than the 5s poll interval and the
 * create→commit race window, but short enough that a genuinely-gone job can't
 * linger. */
export const PENDING_JOB_PRESERVE_MS = 60_000;

/**
 * Reconcile a mapping-job poll response for `examId` with what's already in the
 * store.
 *
 * The poll replaces the whole job set (`setAll`). On its own that erases a job
 * that was optimistically inserted (via `mappingJobReceived`) moments earlier
 * but hasn't committed server-side yet, so the poll returns without it. Because
 * polling only runs while a job is pending, losing the sole pending job also
 * stops polling entirely — the upload then shows no progress until a refresh.
 *
 * To close that race, preserve a locally-present job for this exam that is
 * PENDING, not FINALIZED, absent from the server response, and recently created
 * (within `PENDING_JOB_PRESERVE_MS`). The next poll — once the server catches
 * up — returns the job and it reconciles normally. The recency bound ensures a
 * job that is genuinely gone can't be resurrected indefinitely.
 */
export function mergePolledMappingJobs(
  existingJobs: MappingJob[],
  serverJobs: MappingJob[],
  examId: string,
  nowMs: number,
  maxAgeMs: number = PENDING_JOB_PRESERVE_MS
): MappingJob[] {
  const serverIds = new Set(serverJobs.map((job) => job.id));

  const preserved = existingJobs.filter(
    (job) =>
      job.examId === examId &&
      job.status === JobStatus.PENDING &&
      job.stage !== JobStage.FINALIZED &&
      !serverIds.has(job.id) &&
      nowMs - new Date(job.createdAt).getTime() < maxAgeMs
  );

  return preserved.length ? [...serverJobs, ...preserved] : serverJobs;
}
